import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Star,
  Loader2,
  GripVertical,
  Music,
  UploadCloud,
  BookOpen,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCampaign,
  updateCampaign,
  uploadCampaignAudio,
  clearCampaignAudio,
  mediaUrl,
} from "@/api/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { StorySectionsPanel } from "@/components/admin/StorySectionsPanel";
import { PageEditor } from "@/components/admin/PageEditor";
import { TemplesPanel } from "@/components/admin/TemplesPanel";
import { GlimpseWorkspace } from "@/components/admin/glimpse/GlimpseWorkspace";
import { GlimpseEntryPanel } from "@/components/admin/glimpse/GlimpseEntryPanel";

const ICON_OPTIONS = [
  "book-open",
  "flower-2",
  "sparkles",
  "flame",
  "crown",
  "heart",
  "music",
  "star",
  "gift",
];

const MAX_CTAS = 8;

const DEFAULT_SPONSOR = { enabled: true, prices: { en: 8100, hi: 6400, mr: 5900 } };

const SPONSOR_LANGS = [
  { key: "en", label: "English" },
  { key: "hi", label: "Hindi (हिन्दी)" },
  { key: "mr", label: "Marathi (मराठी)" },
];

const Field = ({ label, ...props }) => (
  <div>
    <label className="mb-1.5 block text-xs text-amber-100/50">{label}</label>
    <input
      {...props}
      className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50"
    />
  </div>
);

export default function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState("landing");
  const [audioBusy, setAudioBusy] = useState(false);
  const audioRef = useRef(null);

  const onAudioFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioBusy(true);
    try {
      const res = await uploadCampaignAudio(id, file);
      setC((p) => ({ ...p, audio_path: res.audio_path, audio_url: "" }));
      toast.success("Audio uploaded");
    } catch {
      toast.error("Could not upload audio (max 25MB, mp3/m4a/aac/ogg/wav)");
    } finally {
      setAudioBusy(false);
      if (audioRef.current) audioRef.current.value = "";
    }
  };

  const removeAudio = async () => {
    setAudioBusy(true);
    try {
      await clearCampaignAudio(id);
      setC((p) => ({ ...p, audio_path: "", audio_url: "" }));
      toast.success("Audio cleared");
    } catch {
      toast.error("Could not clear audio");
    } finally {
      setAudioBusy(false);
    }
  };

  useEffect(() => {
    getCampaign(id)
      .then(setC)
      .catch(() => {
        toast.error("Campaign not found");
        navigate("/admin");
      });
  }, [id, navigate]);

  const setField = (key, val) => setC((p) => ({ ...p, [key]: val }));
  const setBranding = (key, val) =>
    setC((p) => ({ ...p, branding: { ...(p.branding || {}), [key]: val } }));

  const sponsor = c?.sponsor || DEFAULT_SPONSOR;
  const setSponsorEnabled = (val) =>
    setC((p) => ({ ...p, sponsor: { ...(p.sponsor || DEFAULT_SPONSOR), enabled: val } }));
  const setSponsorPrice = (lang, val) =>
    setC((p) => {
      const base = p.sponsor || DEFAULT_SPONSOR;
      return {
        ...p,
        sponsor: {
          ...base,
          prices: { ...base.prices, [lang]: Math.max(0, parseInt(val || "0", 10)) },
        },
      };
    });

  const setCta = (idx, key, val) =>
    setC((p) => {
      const ctas = [...p.ctas];
      ctas[idx] = { ...ctas[idx], [key]: val };
      return { ...p, ctas };
    });

  const addCta = () =>
    setC((p) => {
      if (p.ctas.length >= MAX_CTAS) return p;
      return {
        ...p,
        ctas: [
          ...p.ctas,
          {
            key: `cta-${Date.now()}`,
            icon: "sparkles",
            image: "",
            order: p.ctas.length,
            primary: false,
            visible: true,
            title_en: "",
            title_hi: "",
            desc_en: "",
            desc_hi: "",
            label_en: "",
            label_hi: "",
            url: "#",
          },
        ],
      };
    });

  const removeCta = (idx) =>
    setC((p) => ({ ...p, ctas: p.ctas.filter((_, i) => i !== idx) }));

  const makePrimary = (idx) =>
    setC((p) => ({
      ...p,
      ctas: p.ctas.map((x, i) => ({ ...x, primary: i === idx })),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        temple_name: c.temple_name,
        slug: c.slug,
        active: c.active,
        is_home: !!c.is_home,
        temples: c.temples || [],
        branding: c.branding,
        audio_url: c.audio_url || "",
        sponsor: c.sponsor || DEFAULT_SPONSOR,
        paths_heading_en: c.paths_heading_en || "",
        paths_heading_hi: c.paths_heading_hi || "",
        paths_sub_en: c.paths_sub_en || "",
        paths_sub_hi: c.paths_sub_hi || "",
        ctas: c.ctas.map((x, i) => ({ ...x, order: i, visible: x.visible !== false })),
      };
      const updated = await updateCampaign(id, payload);
      setC(updated);
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return (
      <AdminShell title="Loading…">
        <div className="grid place-items-center py-20 text-amber-100/40">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={c.temple_name}
      subtitle={`Editing microsite /${c.slug} · ${c.ctas.length} of ${MAX_CTAS} cards`}
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 rounded-full border border-amber-300/20 px-4 py-2 text-xs font-medium text-amber-100/80 transition-all hover:border-amber-300/50 hover:bg-amber-400/10"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          {page === "landing" && (
            <button
              data-testid="editor-save-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2 text-xs font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save changes
            </button>
          )}
        </div>
      }
    >
      {/* Page selector — Campaign → Page → workspace */}
      <div className="mb-8 flex items-center gap-3 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-amber-100/60">
          Page
        </label>
        <select
          data-testid="page-selector"
          value={page}
          onChange={(e) => setPage(e.target.value)}
          className="rounded-lg border border-amber-300/20 bg-[#080b16]/60 px-3 py-2 text-sm font-medium text-amber-100 outline-none focus:border-amber-300/50"
        >
          <option value="landing" className="bg-[#0d1226]">Home / Landing Page</option>
          <option value="glimpse" className="bg-[#0d1226]">Bhāgavatam Experience</option>
        </select>
        <span className="text-xs text-amber-100/40">
          {page === "landing"
            ? `Editing microsite /${c.slug}`
            : "Editing the shared Śrīmad-Bhāgavatam glimpse experience"}
        </span>
      </div>

      {page === "landing" && (
        <>
      {/* Details */}
      <div className="mb-8 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-amber-100/80">
          Details & branding
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Campaign name"
            data-testid="editor-name-input"
            value={c.temple_name}
            onChange={(e) => setField("temple_name", e.target.value)}
          />
          <Field
            label="Slug"
            data-testid="editor-slug-input"
            value={c.slug}
            onChange={(e) => setField("slug", e.target.value)}
          />
          <Field
            label="Display name (English)"
            value={c.branding?.display_name_en || ""}
            onChange={(e) => setBranding("display_name_en", e.target.value)}
          />
          <Field
            label="Display name (Hindi)"
            value={c.branding?.display_name_hi || ""}
            onChange={(e) => setBranding("display_name_hi", e.target.value)}
          />
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-amber-100/70">
          <input
            data-testid="editor-active-toggle"
            type="checkbox"
            checked={c.active}
            onChange={(e) => setField("active", e.target.checked)}
            className="h-4 w-4 accent-amber-400"
          />
          Campaign active (microsite live)
        </label>
        <label className="mt-3 flex items-center gap-2 text-sm text-amber-100/70">
          <input
            data-testid="editor-home-toggle"
            type="checkbox"
            checked={!!c.is_home}
            onChange={(e) => setField("is_home", e.target.checked)}
            className="h-4 w-4 accent-amber-400"
          />
          Set as site Home page (drives takekrishnahome.com&nbsp;/)
        </label>
      </div>

      <TemplesPanel
        temples={c.temples || []}
        onChange={(temples) => setField("temples", temples)}
        campaignId={id}
      />

      {/* Ambience audio */}
      <div className="mb-8 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Music className="h-4 w-4 text-amber-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
            Ambience audio
          </h2>
        </div>
        <p className="mb-4 text-xs text-amber-100/40">
          Upload an MP3/M4A or paste a URL to loop as ambience on this microsite.
          The homepage keeps its default sound when nothing is set.
        </p>

        {(c.audio_path || c.audio_url) && (
          <div className="mb-4 rounded-lg border border-amber-300/15 bg-[#080b16]/60 p-3">
            <p className="mb-2 text-xs text-amber-200/80">
              {c.audio_path ? "Uploaded track" : "External URL"}
            </p>
            <audio
              data-testid="editor-audio-preview"
              controls
              src={c.audio_path ? mediaUrl(c.audio_path) : c.audio_url}
              className="h-9 w-full"
            />
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field
            label="External audio URL"
            data-testid="editor-audio-url"
            value={c.audio_url || ""}
            onChange={(e) => setField("audio_url", e.target.value)}
            placeholder="https://…/aarti.mp3 (then Save changes)"
          />
          <div className="flex items-end gap-2">
            <input
              ref={audioRef}
              data-testid="editor-audio-input"
              type="file"
              accept="audio/*"
              onChange={onAudioFile}
              className="hidden"
            />
            <button
              onClick={() => audioRef.current?.click()}
              disabled={audioBusy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/25 px-3.5 py-2 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10 disabled:opacity-50"
            >
              {audioBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <UploadCloud className="h-3.5 w-3.5" />
              )}
              Upload
            </button>
            {(c.audio_path || c.audio_url) && (
              <button
                data-testid="editor-audio-clear"
                onClick={removeAudio}
                disabled={audioBusy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/20 px-3 py-2 text-xs font-medium text-red-300/80 transition-all hover:border-red-400/50 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sponsor a Bhagavatam */}
      <div className="mb-8 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-amber-300" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
              Sponsor a Bhagavatam
            </h2>
          </div>
          <label className="flex items-center gap-2 text-xs text-amber-100/70">
            <input
              data-testid="sponsor-enabled-toggle"
              type="checkbox"
              checked={sponsor.enabled !== false}
              onChange={(e) => setSponsorEnabled(e.target.checked)}
              className="h-4 w-4 accent-amber-400"
            />
            Show on Bhagavatam path
          </label>
        </div>
        <p className="mb-4 text-xs text-amber-100/40">
          Price of one full set per language (₹). The public progress bar fills toward
          one copy; each completed copy adds to the “sponsored” count. Razorpay keys are
          set once in the dashboard’s Global settings.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {SPONSOR_LANGS.map((l) => (
            <div key={l.key}>
              <label className="mb-1.5 block text-xs text-amber-100/50">
                {l.label} price (₹)
              </label>
              <input
                data-testid={`sponsor-price-${l.key}`}
                type="number"
                min="0"
                value={sponsor.prices?.[l.key] ?? ""}
                onChange={(e) => setSponsorPrice(l.key, e.target.value)}
                className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-300/50"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Sacred Ways section heading (the "4 Sacred Ways" title & subtitle) */}
      <div className="mb-8 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-amber-100/80">
          &ldquo;Sacred Ways&rdquo; section heading
        </h2>
        <p className="mb-4 text-xs text-amber-100/40">
          The number of pathways = the CTA cards below (add / hide to control how many
          show). Leave a field blank to use the default text.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Heading (English)</label>
            <input data-testid="paths-heading-en" value={c.paths_heading_en || ""} onChange={(e) => setC((p) => ({ ...p, paths_heading_en: e.target.value }))} placeholder="Four Sacred Ways…" className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-300/50" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Heading (Hindi)</label>
            <input data-testid="paths-heading-hi" value={c.paths_heading_hi || ""} onChange={(e) => setC((p) => ({ ...p, paths_heading_hi: e.target.value }))} className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-300/50" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Sub-heading (English)</label>
            <input value={c.paths_sub_en || ""} onChange={(e) => setC((p) => ({ ...p, paths_sub_en: e.target.value }))} className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-300/50" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Sub-heading (Hindi)</label>
            <input value={c.paths_sub_hi || ""} onChange={(e) => setC((p) => ({ ...p, paths_sub_hi: e.target.value }))} className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-300/50" />
          </div>
        </div>
      </div>

      {/* Bhāgavatam Glimpse entry card (also editable here on the home page) */}
      <GlimpseEntryPanel />

      {/* CTAs */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
          CTA cards ({c.ctas.length}/{MAX_CTAS})
        </h2>
        <button
          data-testid="add-cta-btn"
          onClick={addCta}
          disabled={c.ctas.length >= MAX_CTAS}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-1.5 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> Add card
        </button>
      </div>

      <div className="space-y-4">
        {c.ctas.map((cta, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            data-testid={`cta-editor-${idx}`}
            className={`rounded-2xl border p-5 ${
              cta.primary
                ? "border-amber-300/50 bg-amber-400/[0.04]"
                : "border-amber-300/12 bg-[#0d1226]/70"
            }`}
          >
            <div className="mb-4 flex items-center gap-3">
              <GripVertical className="h-4 w-4 text-amber-100/30" />
              <span className="grid h-7 w-7 place-items-center rounded-full border border-amber-300/30 text-xs font-semibold text-amber-200">
                {idx + 1}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  data-testid={`cta-visible-${idx}`}
                  onClick={() => setCta(idx, "visible", cta.visible === false)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                    cta.visible === false
                      ? "border border-amber-300/25 text-amber-100/50 hover:bg-amber-400/10"
                      : "bg-emerald-400/15 text-emerald-300"
                  }`}
                >
                  {cta.visible === false ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                  {cta.visible === false ? "Hidden" : "Shown"}
                </button>
                <button
                  data-testid={`cta-primary-${idx}`}
                  onClick={() => makePrimary(idx)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                    cta.primary
                      ? "bg-amber-400 text-[#0a0f24]"
                      : "border border-amber-300/25 text-amber-200 hover:bg-amber-400/10"
                  }`}
                >
                  <Star className="h-3.5 w-3.5" />
                  {cta.primary ? "Primary" : "Make primary"}
                </button>
                <button
                  data-testid={`cta-remove-${idx}`}
                  onClick={() => removeCta(idx)}
                  className="rounded-full border border-red-400/20 p-1.5 text-red-300/80 transition-all hover:border-red-400/50 hover:bg-red-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Title (English)"
                value={cta.title_en}
                onChange={(e) => setCta(idx, "title_en", e.target.value)}
              />
              <Field
                label="Title (Hindi)"
                value={cta.title_hi}
                onChange={(e) => setCta(idx, "title_hi", e.target.value)}
              />
              <Field
                label="Description (English)"
                value={cta.desc_en}
                onChange={(e) => setCta(idx, "desc_en", e.target.value)}
              />
              <Field
                label="Description (Hindi)"
                value={cta.desc_hi}
                onChange={(e) => setCta(idx, "desc_hi", e.target.value)}
              />
              <Field
                label="Button label (English)"
                value={cta.label_en}
                onChange={(e) => setCta(idx, "label_en", e.target.value)}
              />
              <Field
                label="Button label (Hindi)"
                value={cta.label_hi}
                onChange={(e) => setCta(idx, "label_hi", e.target.value)}
              />
              <Field
                label="Link URL"
                data-testid={`cta-url-${idx}`}
                value={cta.url}
                onChange={(e) => setCta(idx, "url", e.target.value)}
                placeholder="https://…  (use # for placeholder)"
              />
              <Field
                label="Image URL / path"
                value={cta.image}
                onChange={(e) => setCta(idx, "image", e.target.value)}
                placeholder="/images/cards/…"
              />
              <div>
                <label className="mb-1.5 block text-xs text-amber-100/50">Icon</label>
                <select
                  value={cta.icon}
                  onChange={(e) => setCta(idx, "icon", e.target.value)}
                  className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50"
                >
                  {ICON_OPTIONS.map((ic) => (
                    <option key={ic} value={ic} className="bg-[#0d1226]">
                      {ic}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <StorySectionsPanel campaignId={id} />

      <PageEditor campaignId={id} campaign={c} />
        </>
      )}

      {page === "glimpse" && <GlimpseWorkspace />}
    </AdminShell>
  );
}
